import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { responsiveVideoType } from '../../propTypes';
import { isCurrentBreakpointAtLeast } from '../../selectors';
import selectBestVideoSize from '../../utils/selectBestVideoSize';
import getResponsiveAlternative from '../../utils/getResponsiveAlternative';
import isScrolledIntoView from '../../utils/isScrolledIntoView';
import {UnstyledLink} from "../Widgets/Link"

/**
 * Calls play() on the passed HTMLMediaElement instance, and swallows promise rejections that can
 * occur if the video was already playing - in other words, makes it safe to call play() without
 * first checking whether the video was already playing.
 */
function safePlay(video) {
  const promise = video.play();
  if (promise && promise.catch) {
    promise.catch(() => {}); // ignore playback errors
  }
}

class Video extends PureComponent {
  constructor(props, context) {
    super(props, context);
    const {
      isSmall,
      video,
      viewportWidth,
      viewportHeight,
    } = props;
    this.state = {
      isVideoPlaying: false,
      visible: false,
      ...selectBestVideoSize(video, isSmall, viewportWidth, viewportHeight),
    };
  }

  componentDidMount() {
    this.checkBounds();
  }

  componentDidUpdate(prevProps, prevState) {
    const { paused } = this.props;
    const { alternative, index, visible } = this.state;
    if (paused !== prevProps.paused) {
      this.onPausedChanged();
    } else if (visible !== prevState.visible) {
      this.onVisibleChanged();
    } else if (alternative !== prevState.alternative || index !== prevState.index) {
      this.onVideoIndexChanged();
    }
    this.checkViewportSize(prevProps);
  }

  componentWillUnmount() {
    this.isunmounted = true;
  }

  onVisibleChanged() {
    const { paused } = this.props;
    const { visible } = this.state;
    if (!paused) {
      if (visible) {
        safePlay(this.videoEl);
      } else {
        this.videoEl.pause();
      }
    }
  }

  onPausedChanged() {
    const { paused } = this.props;
    const { visible } = this.state;
    if (paused) {
      this.videoEl.pause();
      setTimeout(() => {
        try {
          this.videoEl.currentTime = 0;
        } catch (e) {
          // ignore InvalidStateError in IE11
        }
      }, 1000);
    } else if (visible) {
      safePlay(this.videoEl);
    }
  }

  onVideoIndexChanged() {
    const { paused, currentTime } = this.props;
    if (!paused) {
      try {
        this.videoEl.currentTime = currentTime;
      } catch (e) {
        // ignore InvalidStateError in IE11
      }
      safePlay(this.videoEl);
    }
  }

  onVideoEnded = () => {
    const { loop, onEnded } = this.props;
    if (onEnded && !loop) {
      onEnded();
    }
  };

  onVideoPlaying = () => {
    this.setState({ isVideoPlaying: true });
    const { onPlaying } = this.props;
    if (onPlaying) {
      onPlaying(this.videoEl.duration);
    }
  };

  onVideoTimeUpdate = () => {
    const { onProgress } = this.props;
    if (onProgress) {
      onProgress(this.videoEl.currentTime);
    }
  };

  getCurrentVideo() {
    const { alternative, index } = this.state;
    const { video } = this.props;
    return getResponsiveAlternative(video, alternative).sizes[index];
  }

  setRef = (el) => {
    if (el) {
      this.videoEl = el;
    }
  };

  checkBounds = () => {
    if (!this.isunmounted) {
      const { visible } = this.state;

      if (isScrolledIntoView(this.videoEl, false)) {
        if (!visible) {
          this.setState({ visible: true });
        }
      } else if (visible) {
        this.setState({ visible: false });
      }
      window.requestAnimationFrame(this.checkBounds); // eslint-disable-line no-undef
    }
  }

  checkViewportSize(prevProps) {
    const { viewportWidth, viewportHeight } = this.props;
    if (viewportWidth !== prevProps.viewportWidth || viewportHeight !== prevProps.viewportHeight) {
      const { isSmall, video } = this.props;
      const nextState = {
        ...selectBestVideoSize(video, isSmall, viewportWidth, viewportHeight),
      };

      const prevState = this.state;
      if (nextState.alternative === prevState.alternative) {
        // only ever go up in size, there's no reason to go down
        nextState.index = Math.max(prevState.index || 0, nextState.index);
      }
      this.setState(nextState);
    }
  }

  calculateVideoStyle() {
    const { isVideoPlaying } = this.state;
    const { width: videoWidth, height: videoHeight } = this.getCurrentVideo();
    const { viewportWidth, viewportHeight } = this.props;

    let scaledWidth = viewportWidth;
    let scale = scaledWidth / videoWidth;
    let scaledHeight = videoHeight * scale;
    if (scaledHeight < viewportHeight) {
      scaledHeight = viewportHeight;
      scale = scaledHeight / videoHeight;
      scaledWidth = videoWidth * scale;
    }

    return {
      width: Math.ceil(scaledWidth),
      height: Math.ceil(scaledHeight),
      left: (viewportWidth - scaledWidth) / 2,
      top: (viewportHeight - scaledHeight) / 2,
      opacity: isVideoPlaying ? 1 : 0,
    };
  }

  render() {
    const { className, loop, link, controls } = this.props;
    const video = this.getCurrentVideo();
    const videoStyle = this.calculateVideoStyle();
    const hasLink = link && link.url;

    const videoElement = <video
        preload="none"
        src={video.url}
        autoPlay={false}
        loop={loop}
        muted
        playsInline
        onPlaying={this.onVideoPlaying}
        onTimeUpdate={this.onVideoTimeUpdate}
        onEnded={this.onVideoEnded}
        className={className}
        ref={this.setRef}
        style={videoStyle}
        controls={controls}
    />

    return hasLink ?
        <UnstyledLink link={link}>{videoElement}</UnstyledLink> : videoElement
  }
}

Video.propTypes = {
  className: PropTypes.string,
  currentTime: PropTypes.number,
  loop: PropTypes.bool,
  isSmall: PropTypes.bool.isRequired,
  onEnded: PropTypes.func,
  onPlaying: PropTypes.func,
  onProgress: PropTypes.func,
  paused: PropTypes.bool,
  video: responsiveVideoType,
  viewportHeight: PropTypes.number.isRequired,
  viewportWidth: PropTypes.number.isRequired,
  controls: PropTypes.bool
};

Video.defaultProps = {
  className: undefined,
  currentTime: 0,
  loop: false,
  paused: false,
  onEnded: undefined,
  onPlaying: undefined,
  onProgress: undefined,
  video: null,
  controls: false
};

const mapStateToProps = state => ({
  isSmall: !isCurrentBreakpointAtLeast(state, 'tablet'),
});

export default connect(mapStateToProps)(Video);



// WEBPACK FOOTER //
// ./src/components/Widgets/Video.js